import { Box } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilCallback, useRecoilValue, useRecoilValueLoadable } from "recoil";

import { Document, DocumentType } from "@/api/generated";
import { NEW_MANAGEMENT_TYPE_KEY_VALUES } from "@/api/mappings/property";
import { Loading } from "@/components/Loading";
import { AccordionDetailItemRow } from "@/components/management/AccordionDetailItemRow";
import { DetailSection, useDetailStyles } from "@/components/management/DetailSection";
import { DocumentTypeOptions } from "@/constants/management";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { PropertyEditPageState } from "@/view_models/atoms";
import { PropertyIdToThisMonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";
import { SelectedProperty } from "@/view_models/propertySelectors";

// 保有中物件詳細　物件情報
export const ManagementDetailPropertyInfo: React.FC = () => {
  const classes = useDetailStyles();
  const history = useHistory();
  const property = useRecoilValue(SelectedProperty);
  const managementTypeLabel =
    NEW_MANAGEMENT_TYPE_KEY_VALUES.find((item) => item.key === property.property.management_type)?.value || "-";
  const [documents, setDocuments] = React.useState<Map<DocumentType, Document[]>>(null);

  // useRecoilCallbackを使用して非同期の状態更新を行う
  const updatePropertyEditPageState = useRecoilCallback(({ snapshot, set }) => async (newState) => {
    set(PropertyEditPageState, newState);
    // 状態が更新されるのを待つ
    await snapshot.getPromise(PropertyEditPageState);
  });

  // ドキュメントの一覧取得
  const getDocuments = async () => {
    const fp = PropallyApiFactory();
    try {
      const newDocuments = new Map();
      await Promise.all(
        DocumentTypeOptions.map(async (documentType) => {
          const { data } = await fp.v1PropertiesPropertyIdDocumentsDocumentTypeFilesGet(
            property.id,
            documentType.value,
            {
              withCredentials: true,
            }
          );
          newDocuments.set(documentType.value, data);
        })
      );
      setDocuments(newDocuments);
    } catch (e) {
      console.error(e);
      return;
    }
  };

  // 物件詳細情報編集ボタンクリック時
  const onClickEditProrpertyInfo = async () => {
    await updatePropertyEditPageState("property_info");
    history.push("/edit_property");
  };

  // 購入時情報編集ボタンクリック時
  const onClickEditPurchaseInfo = async () => {
    await updatePropertyEditPageState("purchase_info");
    history.push("/edit_property");
  };

  React.useEffect(() => {
    // ドキュメントの取得
    if (!documents) {
      getDocuments();
    }
  }, [property.id]);

  const this_month_sheet_map_loadable = useRecoilValueLoadable(PropertyIdToThisMonthlyBalanceSheet(null));
  if (this_month_sheet_map_loadable.state === "loading") {
    return <Loading />;
  }
  if (this_month_sheet_map_loadable.state === "hasError") {
    throw this_month_sheet_map_loadable.contents;
  }
  // const this_month_sheet = this_month_sheet_map_loadable.contents.get(property.id);

  // 価格を万円表記に変換
  const getPriceFormat = (price: number) => {
    return `${Math.round(price / 10000).toLocaleString()}万円`;
  };

  return (
    <Box display="grid" gridGap={24} pb={5}>
      <DetailSection title="基本情報" onEdit={onClickEditProrpertyInfo} qaId="management-detail-property-info-edit">
        <Box className={classes.rows}>
          {/* 物件詳細 */}
          <AccordionDetailItemRow v2 title="マンション・アパート名" value={property.property.name} />
          {property.property.property_category !== "一棟物件" && (
            <>
              <AccordionDetailItemRow v2 title="号室" value={property.property.unit_detail.room_number} />
              <AccordionDetailItemRow v2 title="所在階" value={`${property.property.unit_detail.floor}階`} />
            </>
          )}
          <AccordionDetailItemRow v2 title="都道府県" value={property.property.prefecture} />
          <AccordionDetailItemRow v2 title="市区町村" value={property.property.city} />
          <AccordionDetailItemRow v2 title="番地" value={property.property.town ? property.property.town : "-"} />
          <AccordionDetailItemRow
            v2
            title="路線名"
            value={property.property.railway ? property.property.railway : "-"}
          />
          <AccordionDetailItemRow
            v2
            title="駅名"
            value={property.property.station ? property.property.station : "-駅"}
          />
          <AccordionDetailItemRow
            v2
            title="駅徒歩"
            value={property.property.walk ? `${property.property.walk}分` : "-分"}
          />
          <AccordionDetailItemRow
            v2
            title="築年数"
            value={
              property.property.built_at ? `${dayjs().year() - dayjs(property.property.built_at).year()}年` : "-年"
            }
          />
          {property.property.property_category !== "一棟物件" && (
            <>
              <AccordionDetailItemRow
                v2
                title="占有面積"
                value={
                  property.property.unit_detail.occupied_area_m2
                    ? `${property.property.unit_detail.occupied_area_m2}㎡`
                    : "-㎡"
                }
              />
              <AccordionDetailItemRow v2 title="間取り" value={property.property.unit_detail.layout} />
            </>
          )}
          <AccordionDetailItemRow v2 title="構造" value={property.property.structure} />
          <AccordionDetailItemRow v2 title="管理形態" value={managementTypeLabel} />
          {/* 希望売却価格 */}
          <AccordionDetailItemRow
            v2
            title="希望売却価格（あれば）"
            value={property.property.asking_price ? `${property.property.asking_price.toLocaleString()}円` : "-円"}
          />
        </Box>
      </DetailSection>
      <DetailSection title="購入時情報" onEdit={onClickEditPurchaseInfo} qaId="management-detail-purchase-info-edit">
        <Box className={classes.rows}>
          <AccordionDetailItemRow
            v2
            title="購入時期"
            value={property.property.bought_at ? dayjs(property.property.bought_at).format("YYYY年MM月DD日") : "未設定"}
          />
          <AccordionDetailItemRow
            v2
            title="購入価格（税込）"
            value={getPriceFormat(property.property.total_price_of_property)}
          />
          <AccordionDetailItemRow v2 title="うち消費税" value={getPriceFormat(property.property.priceof_tax)} />
          <AccordionDetailItemRow
            v2
            title="うち建物代（税抜）"
            value={getPriceFormat(property.property.priceof_building)}
          />
          <AccordionDetailItemRow v2 title="うち土地代" value={getPriceFormat(property.property.priceof_land)} />
          <AccordionDetailItemRow
            v2
            title="建物付属設備代（税抜）"
            value={
              property.property.priceof_facilities ? getPriceFormat(property.property.priceof_facilities) : "-万円"
            }
            showFinalTaxReturn={!property.property.priceof_facilities}
          />
          <AccordionDetailItemRow
            v2
            title="購入時不動産取得税"
            value={
              property.property.property_acquisition_tax
                ? getPriceFormat(property.property.property_acquisition_tax)
                : "未設定"
            }
            showFinalTaxReturn={!property.property.property_acquisition_tax}
          />
          <AccordionDetailItemRow
            v2
            title="不動産取得税の支払い月"
            value={
              property.property.property_acquisition_tax_payment_month
                ? dayjs(property.property.property_acquisition_tax_payment_month).format("YYYY年MM月")
                : "未設定"
            }
            showFinalTaxReturn={!property.property.property_acquisition_tax_payment_month}
          />
          <AccordionDetailItemRow
            v2
            title="購入時その他諸費用"
            value={
              property.property.misc_expences_at_bought
                ? getPriceFormat(property.property.misc_expences_at_bought)
                : "未設定"
            }
          />
        </Box>
      </DetailSection>
      <DetailSection title="書類">
        <Box className={classes.rows}>
          {/* {documents && (
              <> */}
          <AccordionDetailItemRow
            v2
            title="物件パンフレット"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_1)?.length > 0}
            documentType={DocumentType.NUMBER_1}
          />
          <AccordionDetailItemRow
            v2
            title="売買契約書"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_2)?.length > 0}
            documentType={DocumentType.NUMBER_2}
          />
          <AccordionDetailItemRow
            v2
            title="重要事項説明書"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_3)?.length > 0}
            documentType={DocumentType.NUMBER_3}
          />
          <AccordionDetailItemRow
            v2
            title="賃貸借契約書"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_6)?.length > 0}
            documentType={DocumentType.NUMBER_6}
          />
          <AccordionDetailItemRow
            v2
            title="賃貸借管理委託契約書"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_7)?.length > 0}
            documentType={DocumentType.NUMBER_7}
          />
          <AccordionDetailItemRow
            v2
            title="返済予定表"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_5)?.length > 0}
            documentType={DocumentType.NUMBER_5}
          />
          <AccordionDetailItemRow
            v2
            title="納税通知書(固都税/不動産取得税)"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_10)?.length > 0}
            documentType={DocumentType.NUMBER_10}
          />
          <AccordionDetailItemRow
            v2
            title="保険証書(火災/地震)"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_8)?.length > 0}
            documentType={DocumentType.NUMBER_8}
          />
          <AccordionDetailItemRow
            v2
            title="その他（領収書など）"
            isDocument
            hasDocument={documents?.get(DocumentType.NUMBER_11)?.length > 0}
            documentType={DocumentType.NUMBER_11}
          />
          {/* </>
            )} */}
        </Box>
      </DetailSection>
    </Box>
  );
};
